/**
 * Internal-only UI switches for the Clause Library, Section-Level Review and
 * the other areas below, one per module. (The Obligation Tracker and the
 * Receivables Recovery are no longer switched.) With a switch off,
 * everything of that module is hidden from the UI: its menu entries and pages (the routes answer "not
 * found"), its dashboard and overview widgets, its tabs, sections and modal
 * windows elsewhere (a contract's obligations tab, the AI's suggested
 * obligations, the calendar, the reminder cards) and its notifications.
 * The backend enforces its own permissions and plan regardless.
 *
 * Off by default. Turn one on from the browser console and reload:
 *
 *   localStorage.setItem("isEmeisClause", "true")
 *   localStorage.setItem("isEmeisCalendar", "true")
 *   localStorage.setItem("isEmeisSubscription", "true")
 *   localStorage.setItem("isEmeisReview", "true")
 *   localStorage.setItem("isEmeisBulk", "true")
 *   localStorage.setItem("isEmeisSignature", "true")
 *   localStorage.setItem("isEmeisMonitor", "true")
 *
 * "isEmeisCalendar" covers My Calendar and the notification preferences
 * (Notifications → Preferences, and the link in the user menu).
 * "isEmeisSubscription" covers licensing: Subscription & Usage, License
 * Management, the plan banner, the plan section of Organisation details and
 * licensing notifications (it took these over from "isEmeis").
 * "isEmeisReview" covers section-level review inside a contract: the
 * selection toolbar, the Assign for Review dialog, the review sidebar (My
 * Assignments / All Reviews), the review summary, the in-document review
 * highlights and the section review notifications.
 * "isEmeisBulk" covers bulk upload of contracts and legacy contracts: the two
 * menu entries under New Contract and their pages (/contracts/bulk and
 * /contracts/bulk-legacy).
 * "isEmeisSignature" covers Pactline Native Signature: the native option in
 * the Send for Signature dialog, signature field placement in Contract
 * Studio, the signing ceremony page (/sign/:requestId), the saved-signature
 * settings and Administration → Settings → E-Signature. DocuSign and
 * emSigner stay available regardless.
 * "isEmeisMonitor" covers Administration → Application Status (super admin
 * only): the menu entry and the /admin/application-status page that lists,
 * controls and tails the services of a server over SSH.
 *
 * and off again with localStorage.removeItem(...).
 */
export const EMEIS_CLAUSE_FLAG_KEY = "isEmeisClause";
export const EMEIS_CALENDAR_FLAG_KEY = "isEmeisCalendar";
export const EMEIS_SUBSCRIPTION_FLAG_KEY = "isEmeisSubscription";
export const EMEIS_REVIEW_FLAG_KEY = "isEmeisReview";
export const EMEIS_BULK_FLAG_KEY = "isEmeisBulk";
export const EMEIS_SIGNATURE_FLAG_KEY = "isEmeisSignature";
export const EMEIS_MONITOR_FLAG_KEY = "isEmeisMonitor";

const isOn = (key: string): boolean => {
  try {
    return localStorage.getItem(key) === "true";
  } catch {
    return false;
  }
};

/** Library → Clause Templates and the clause panels inside the editors. */
export const isEmeisClauseEnabled = (): boolean => isOn(EMEIS_CLAUSE_FLAG_KEY);

/** My Calendar and the notification preferences page. */
export const isEmeisCalendarEnabled = (): boolean =>
  isOn(EMEIS_CALENDAR_FLAG_KEY);

/** Subscription & Usage, License Management, the plan banner and licensing notifications. */
export const isEmeisSubscriptionEnabled = (): boolean =>
  isOn(EMEIS_SUBSCRIPTION_FLAG_KEY);

/** Section-level review inside a contract: toolbar, sidebar, highlights, dialogs and its notifications. */
export const isEmeisReviewEnabled = (): boolean => isOn(EMEIS_REVIEW_FLAG_KEY);

/** Bulk upload of contracts and legacy contracts: its menu entries and pages. */
export const isEmeisBulkEnabled = (): boolean => isOn(EMEIS_BULK_FLAG_KEY);

/** Pactline Native Signature: the native signing option, field placement, the signing ceremony and its admin settings. */
export const isEmeisSignatureEnabled = (): boolean =>
  isOn(EMEIS_SIGNATURE_FLAG_KEY);

/** Administration → Application Status: its menu entry and page (super admin). */
export const isEmeisMonitorEnabled = (): boolean => isOn(EMEIS_MONITOR_FLAG_KEY);
